import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import ProjectView from "@/pages/ProjectView";
import Pricing from "@/pages/Pricing";
import Account from "@/pages/Account";
import Status from "@/pages/Status";
import CaseStudies from "@/pages/CaseStudies";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import Legal from "@/pages/Legal";
import Press from "@/pages/Press";
import CrispChat from "@/components/CrispChat";
import Terms from "@/pages/Terms";
import GuidedTour from "@/components/GuidedTour";
import { Toaster } from "@/components/ui/sonner";
import { Sentry } from "@/lib/sentry";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

/**
 * Fires a GA4 page_view on every SPA route change. The initial page_view
 * is auto-fired by the gtag('config', ...) call in index.html, so this
 * effect skips the first render and only tracks subsequent navigations.
 */
function GaPageView() {
  const location = useLocation();
  const isFirst = React.useRef(true);
  React.useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  return null;
}

function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100 p-6" data-testid="app-error-boundary">
      <div className="max-w-md bg-white border border-zinc-200 p-8">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30]">// Something went wrong</div>
        <h1 className="font-display font-black text-2xl mt-3">ARISSAI hit an unexpected error.</h1>
        <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
          Our team has been notified automatically. Please reload the page — if the problem persists, contact <a href="mailto:hello@arissai.com" className="font-mono underline">hello@arissai.com</a>.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 h-11 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800"
          data-testid="error-boundary-reload"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <div className="App" data-testid="app-shell">
        <AuthProvider>
          <BrowserRouter>
            <GaPageView />
            <CrispChat />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/admin" element={<Protected><Admin /></Protected>} />
              <Route path="/status" element={<Status />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/press" element={<Press />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/projects/:projectId" element={<Protected><ProjectView /></Protected>} />
              <Route path="/projects/:projectId/:lens" element={<Protected><ProjectView /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <GuidedTour />
          </BrowserRouter>
          <Toaster position="top-right" />
        </AuthProvider>
      </div>
    </Sentry.ErrorBoundary>
  );
}

export default App;
